import { useMemo, type FC } from 'react';
import { Card, Descriptions, Form } from 'components';
import type { MeterModelFormValues } from '../../types';
import { Col, Flex, Grid, Row, Skeleton } from 'antd';
import { MeterModelFilesCard } from '../../components/MeterModelFilesCard';
import { InbuiltOutputs } from '../../components/InbuiltOutputs';
import { JsonEditorField } from '../../components/DetailsEditor';
import { MeterModelCompatibleModulesFilter } from '../../components/Filter';
import { CompatibleMeterModulesTable } from '../../components/Table';
import { useUpdateMeterModel } from 'api/hooks/meter-models/useUpdateModel';
import { useCreateMeterModel } from 'api/hooks/meter-models/useCreateModel';
import type { CreateMeterModelDto, UpdateMeterModelDto } from '@enmon/commons/lib/tools';
import { DriveFolderType } from '@enmon/commons/lib/tools';
import { isNil } from 'ramda';
import { MODEL_FORM_ID } from '../../constants';
import { getModelDescriptionColumns } from '../../utils/getDescriptionsConfig';
import { useModelDetailContext } from '@/routes/reseller-data/routes/model-library/routes/models/routes/detail/store';
import { useHistory } from 'react-router-dom';
import { useIsFetching } from 'react-query';
import { QUERY_KEYS } from '@/utils/queryKeys';
import { useIsMagicRole } from '@/hooks/useUser';
import type { Paths } from 'type-fest';
import type { FormInstance } from 'antd';

const { useBreakpoint } = Grid;

interface MeterModelOverviewTabProps {
  form: FormInstance<MeterModelFormValues>;
  setIsFormChanged: (changed: boolean) => void;
}

export const MeterModelOverviewTab: FC<MeterModelOverviewTabProps> = ({ form, setIsFormChanged }) => {
  const { model, modelId } = useModelDetailContext();
  const isLoadingModel = useIsFetching(QUERY_KEYS.meterModels.one(modelId), { exact: true }) > 0;

  const history = useHistory();
  const breakpoints = useBreakpoint();
  const isMagicRole = useIsMagicRole();

  const isCreateFormMode = !modelId;

  const { mutate: updateModel, isLoading: isUpdatingModel } = useUpdateMeterModel();
  const { mutate: createModel, isLoading: isCreatingModel } = useCreateMeterModel();

  const descriptionData = useMemo(
    () => ({
      ...model,
      manufacturerId: model?.manufacturer?.id ?? null,
      inbuiltOutputs: model?.details?.inbuilt_outputs ?? [],
    }),
    [model],
  );

  // Columns config
  const modelDescriptionsColumn = getModelDescriptionColumns();

  // Handlers
  const handleCreateModel = (values: MeterModelFormValues) => {
    const dto = { ...values, manufacturer: values.manufacturerId } as CreateMeterModelDto;

    createModel(dto, {
      onSuccess: responseData => {
        history.replace(`/reseller-data/model-library/model/${responseData.id}`);
      },
    });
  };

  const handleUpdateModel = (values: MeterModelFormValues) => {
    if (!modelId) throw new Error('Model is not defined in update context');

    // Convert undefined to null for DTO compatibility
    const dtoValues = Object.fromEntries(
      Object.entries(values).map(([key, value]) => [key, isNil(value) ? null : value]),
    );

    const dto = { ...dtoValues, manufacturer: dtoValues.manufacturerId } as UpdateMeterModelDto;

    updateModel({ id: modelId, values: dto }, { onSuccess: () => setIsFormChanged(false) });
  };

  const isLoading = isLoadingModel || isUpdatingModel || isCreatingModel;

  if (isLoading) <Skeleton />;

  const DocumentFiles = (
    <MeterModelFilesCard model={model} folderType={DriveFolderType.Docs} accept="application/pdf" />
  );

  return (
    <Form<MeterModelFormValues>
      form={form}
      id={MODEL_FORM_ID}
      initialValues={descriptionData}
      onFinish={values => (isCreateFormMode ? handleCreateModel(values) : handleUpdateModel(values))}
      onValuesChange={() => setIsFormChanged(true)}
    >
      <Row gutter={[8, 8]}>
        <Col lg={16} md={24} xl={16} xs={24}>
          <Flex vertical gap={8}>
            <Row gutter={[8, 8]}>
              <Col lg={24} md={12} xl={24} xs={24} xxl={12}>
                <Card>
                  <Descriptions
                    column={1}
                    columns={modelDescriptionsColumn}
                    data={descriptionData}
                    isEditing={isMagicRole}
                    labelWidth={150}
                    layout="vertical"
                    size="small"
                  />
                </Card>
              </Col>

              {!isCreateFormMode && (
                <Col lg={24} md={12} xl={24} xs={24} xxl={12}>
                  <MeterModelFilesCard model={model} folderType={DriveFolderType.Photos} accept="image/*" />
                </Col>
              )}
            </Row>
            {(breakpoints.xs || breakpoints.sm || breakpoints.md) &&
              !breakpoints.lg &&
              !isCreateFormMode &&
              DocumentFiles}

            {!isCreateFormMode && (
              <Card loading={isLoading} title="Inbuilt outputs">
                <InbuiltOutputs outputs={descriptionData.inbuiltOutputs} />
              </Card>
            )}

            <Card loading={isLoading} title="Meter model details">
              <Form.Item name={'details' satisfies Paths<MeterModelFormValues>}>
                <JsonEditorField isMagicRole={isMagicRole} />
              </Form.Item>
            </Card>

            {!isCreateFormMode && (
              <Card title="Compatible meter modules">
                <Flex vertical gap={16}>
                  <MeterModelCompatibleModulesFilter />

                  <CompatibleMeterModulesTable />
                </Flex>
              </Card>
            )}
          </Flex>
        </Col>

        {breakpoints.lg && !isCreateFormMode && (
          <Col lg={8} md={24} xl={8} xs={24}>
            {DocumentFiles}
          </Col>
        )}
      </Row>
    </Form>
  );
};
